import { Alert, Button, Spin, Tag } from 'antd'
import React, { useEffect, useMemo, useState } from 'react'
import { useCatchBoardDealContext } from './useCatchBoardDealContext'
import {
  formatCatchBoardDate,
  formatCatchBoardRelativeTime,
} from 'src/utils/catchBoardMappers'
import { formatCatchBoardDuplicateVenueWarning } from 'src/utils/catchBoardDuplicateVenues'
import { CATCH_BOARD_TOOLTIPS } from 'src/utils/catchBoardTooltips'
import {
  formatOpportunityOccurredAt,
  formatOpportunityStatusLabel,
  formatOpportunityTypeLabel,
} from 'src/utils/crowsNestOpportunityFeed'
import styled from 'styled-components/macro'

/**
 * Lazy Spartacus + Crow's Nest context for an open Catch Board deal.
 * Loads on modal open (and per venue tab) — not with the board.
 */
const CatchBoardDealContextPanel = ({
  dealId = null,
  linkedVenues = [],
  primaryVenueId = null,
  enabled = true,
  refreshNonce = 0,
}) => {
  const venueTabs = useMemo(
    () =>
      (linkedVenues || []).map(venue => ({
        venueId: String(venue.venueId),
        name: venue.name || venue.venueName || 'Venue',
        isPrimary:
          String(venue.venueId) === String(primaryVenueId || '') ||
          Boolean(venue.isPrimary),
      })),
    [linkedVenues, primaryVenueId],
  )

  const defaultVenueId = useMemo(() => {
    if (!venueTabs.length) {
      return null
    }
    const primary = venueTabs.find(tab => tab.isPrimary)
    return primary?.venueId || venueTabs[0].venueId
  }, [venueTabs])

  const [selectedVenueId, setSelectedVenueId] = useState(defaultVenueId)

  useEffect(() => {
    if (!defaultVenueId) {
      setSelectedVenueId(null)
      return
    }
    setSelectedVenueId(prev => {
      if (prev && venueTabs.some(tab => tab.venueId === prev)) {
        return prev
      }
      return defaultVenueId
    })
  }, [defaultVenueId, venueTabs])

  const { context, loading, error, refresh } = useCatchBoardDealContext({
    dealId,
    venueId: selectedVenueId,
    enabled: Boolean(enabled && dealId && selectedVenueId),
  })

  useEffect(() => {
    if (!refreshNonce) {
      return undefined
    }
    refresh()
    return undefined
  }, [refreshNonce, refresh])

  const venueContext = useMemo(() => {
    if (!selectedVenueId) {
      return null
    }
    return (
      context.venues.find(
        venue => String(venue.venueId) === String(selectedVenueId),
      ) || null
    )
  }, [context.venues, selectedVenueId])

  const duplicateWarnings = context.duplicateVenueWarnings || []

  if (!enabled || !dealId) {
    return null
  }

  return (
    <Section data-testid='catch-board-deal-context-panel'>
      <SectionHeader>
        <div>
          <SectionTitle>Venue context</SectionTitle>
          <SectionHint>
            Spartacus snapshot, users, notes, and Crow&apos;s Nest — loaded when
            you open the deal.
          </SectionHint>
        </div>
        {selectedVenueId ? (
          <Button
            size='small'
            onClick={() => refresh()}
            disabled={loading}
            data-testid='catch-board-deal-context-refresh'
          >
            Refresh
          </Button>
        ) : null}
      </SectionHeader>

      {!venueTabs.length ? (
        <EmptyHint data-testid='catch-board-deal-context-empty'>
          Link a venue to load Spartacus and Crow&apos;s Nest context.
        </EmptyHint>
      ) : (
        <>
          {duplicateWarnings.length ? (
            <Alert
              type='warning'
              showIcon
              style={{ marginBottom: 10 }}
              data-testid='catch-board-deal-context-duplicates'
              message='Venue also on other open deals'
              description={
                <>
                  <WarnOnlyHint data-testid='catch-board-deal-context-duplicates-hint'>
                    {CATCH_BOARD_TOOLTIPS.duplicateVenueWarn}
                  </WarnOnlyHint>
                  <WarningList>
                    {duplicateWarnings.map(warning => (
                      <li key={warning.venueId || 'venue'}>
                        {formatDuplicateWarning(warning, venueTabs)}
                      </li>
                    ))}
                  </WarningList>
                </>
              }
            />
          ) : null}

          {venueTabs.length > 1 ? (
            <TabRow data-testid='catch-board-deal-context-venue-tabs'>
              {venueTabs.map(tab => (
                <TabButton
                  key={tab.venueId}
                  type='button'
                  $active={tab.venueId === selectedVenueId}
                  onClick={() => setSelectedVenueId(tab.venueId)}
                  data-testid={`catch-board-deal-context-tab-${tab.venueId}`}
                >
                  {tab.name}
                  {tab.isPrimary ? <PrimaryMark>Primary</PrimaryMark> : null}
                </TabButton>
              ))}
            </TabRow>
          ) : null}

          {loading ? (
            <LoadingWrap data-testid='catch-board-deal-context-loading'>
              <Spin size='small' />
              <span>Loading venue context…</span>
            </LoadingWrap>
          ) : null}

          {error ? (
            <Alert
              type='error'
              showIcon
              message={error}
              style={{ marginBottom: 8 }}
              data-testid='catch-board-deal-context-error'
            />
          ) : null}

          {!loading && !error && venueContext ? (
            <ContextBody data-testid='catch-board-deal-context-body'>
              <SnapshotBlock snapshot={venueContext.snapshot} />
              <UsersBlock users={venueContext.users} />
              <NotesBlock notes={venueContext.notes} />
              <OpportunitiesBlock
                opportunities={venueContext.opportunities}
              />
            </ContextBody>
          ) : null}

          {!loading &&
          !error &&
          selectedVenueId &&
          !venueContext &&
          context.dealId ? (
            <EmptyHint>No context returned for this venue.</EmptyHint>
          ) : null}

          {Array.isArray(context.linkedOpportunities) &&
          context.linkedOpportunities.length ? (
            <LinkedOppsBlock
              opportunities={context.linkedOpportunities}
            />
          ) : null}
        </>
      )}
    </Section>
  )
}

function SnapshotBlock({ snapshot }) {
  if (!snapshot) {
    return null
  }

  const metrics = [
    { label: 'Plan', value: snapshot.plan || '—' },
    {
      label: 'Orders',
      value:
        snapshot.ordersInPeriod == null ? '—' : String(snapshot.ordersInPeriod),
    },
    {
      label: 'Upgrade',
      value:
        snapshot.upgradeScore == null ? '—' : String(snapshot.upgradeScore),
    },
    {
      label: 'Risk',
      value: snapshot.riskScore == null ? '—' : String(snapshot.riskScore),
    },
    {
      label: 'Suppliers',
      value:
        snapshot.activeSuppliers == null
          ? '—'
          : String(snapshot.activeSuppliers),
    },
    {
      label: 'Last ordered',
      value: formatCatchBoardDate(snapshot.lastOrderedAt),
    },
  ]

  return (
    <Block data-testid='catch-board-deal-context-snapshot'>
      <BlockTitle>
        {snapshot.name || 'Spartacus snapshot'}
        {snapshot.xeroConnected ? (
          <Tag color='green' style={{ marginLeft: 6 }}>
            Xero
          </Tag>
        ) : null}
      </BlockTitle>
      <MetricsGrid>
        {metrics.map(metric => (
          <Metric key={metric.label}>
            <MetricLabel>{metric.label}</MetricLabel>
            <MetricValue>{metric.value}</MetricValue>
          </Metric>
        ))}
      </MetricsGrid>
    </Block>
  )
}

function UsersBlock({ users }) {
  return (
    <Block data-testid='catch-board-deal-context-users'>
      <BlockTitle>Users ({users.length})</BlockTitle>
      {!users.length ? (
        <EmptyHint>No venue users found.</EmptyHint>
      ) : (
        <CompactList>
          {users.slice(0, 8).map(user => (
            <CompactItem
              key={user.accountId || `${user.email}-${user.name}`}
            >
              <ItemMain>
                {user.name || user.email || 'User'}
                {user.isOwner ? <Tag style={{ marginLeft: 6 }}>Owner</Tag> : null}
              </ItemMain>
              <ItemMeta>
                {[user.role, user.email, user.lastSeenLabel]
                  .filter(Boolean)
                  .join(' · ')}
              </ItemMeta>
            </CompactItem>
          ))}
          {users.length > 8 ? (
            <EmptyHint>+{users.length - 8} more</EmptyHint>
          ) : null}
        </CompactList>
      )}
    </Block>
  )
}

function NotesBlock({ notes }) {
  return (
    <Block data-testid='catch-board-deal-context-notes'>
      <BlockTitle>Notes ({notes.length})</BlockTitle>
      {!notes.length ? (
        <EmptyHint>No Spartacus notes yet.</EmptyHint>
      ) : (
        <CompactList>
          {notes.slice(0, 5).map(note => (
            <CompactItem key={note.noteId || note.createdAt}>
              <ItemMain title={note.noteText}>
                {truncate(note.noteText, 120) || 'Note'}
                {note.pinned ? (
                  <Tag color='gold' style={{ marginLeft: 6 }}>
                    Pinned
                  </Tag>
                ) : null}
              </ItemMain>
              <ItemMeta>
                {[
                  note.authorName,
                  note.noteType,
                  note.createdAt
                    ? formatCatchBoardRelativeTime(note.createdAt)
                    : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </ItemMeta>
            </CompactItem>
          ))}
          {notes.length > 5 ? (
            <EmptyHint>+{notes.length - 5} more</EmptyHint>
          ) : null}
        </CompactList>
      )}
    </Block>
  )
}

function OpportunitiesBlock({ opportunities }) {
  return (
    <Block data-testid='catch-board-deal-context-opps'>
      <BlockTitle>Crow&apos;s Nest ({opportunities.length})</BlockTitle>
      {!opportunities.length ? (
        <EmptyHint>No recent opportunities for this venue.</EmptyHint>
      ) : (
        <CompactList>
          {opportunities.slice(0, 5).map(opp => (
            <CompactItem key={opp.id || `${opp.type}-${opp.occurredAt}`}>
              <ItemMain>
                {formatOpportunityTypeLabel(opp.type)}
                {opp.featureName ? ` · ${opp.featureName}` : ''}
                {opp.status ? (
                  <Tag style={{ marginLeft: 6 }}>
                    {formatOpportunityStatusLabel(opp.status)}
                  </Tag>
                ) : null}
              </ItemMain>
              <ItemMeta>
                {[
                  formatOpportunityOccurredAt(opp.occurredAt),
                  opp.suggestedFollowUp
                    ? truncate(opp.suggestedFollowUp, 80)
                    : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </ItemMeta>
            </CompactItem>
          ))}
          {opportunities.length > 5 ? (
            <EmptyHint>+{opportunities.length - 5} more</EmptyHint>
          ) : null}
        </CompactList>
      )}
    </Block>
  )
}

function LinkedOppsBlock({ opportunities }) {
  return (
    <Block data-testid='catch-board-deal-context-linked-opps'>
      <BlockTitle>
        Linked opportunities ({opportunities.length})
      </BlockTitle>
      <CompactList>
        {opportunities.slice(0, 5).map(opp => {
          const id = opp.id || opp._id || String(opp)
          return (
            <CompactItem key={id}>
              <ItemMain>
                {formatOpportunityTypeLabel(opp.type) || 'Opportunity'}
                {opp.featureName ? ` · ${opp.featureName}` : ''}
              </ItemMain>
              <ItemMeta>
                {[
                  formatOpportunityStatusLabel(opp.status),
                  formatOpportunityOccurredAt(opp.occurredAt),
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </ItemMeta>
            </CompactItem>
          )
        })}
      </CompactList>
    </Block>
  )
}

function formatDuplicateWarning(warning, venueTabs) {
  const venueNamesById = {}
  ;(venueTabs || []).forEach(tab => {
    if (tab?.venueId) {
      venueNamesById[String(tab.venueId)] = tab.name || `Venue ${tab.venueId}`
    }
  })
  return formatCatchBoardDuplicateVenueWarning(warning, { venueNamesById })
}

function truncate(value, max) {
  const text = String(value || '').trim()
  if (text.length <= max) {
    return text
  }
  return `${text.slice(0, max - 1)}…`
}

const Section = styled.div`
  margin-top: 8px;
  padding-top: 12px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
`

const SectionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
`

const SectionTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.colours.broccoliGreen || '#1a1a1a'};
`

const SectionHint = styled.p`
  margin: 4px 0 10px;
  font-size: 12px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colours.stormGrey || '#666'};
`

const EmptyHint = styled.p`
  margin: 0;
  font-size: 12px;
  color: ${({ theme }) => theme.colours.stormGrey || '#666'};
`

const WarnOnlyHint = styled.p`
  margin: 0 0 8px;
  font-size: 13px;
`

const WarningList = styled.ul`
  margin: 0;
  padding-left: 1.1rem;
`

const TabRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 10px;
`

const TabButton = styled.button`
  appearance: none;
  border: 1px solid
    ${({ $active, theme }) =>
      $active
        ? theme.colours.broccoliGreen || '#2d6a4f'
        : 'rgba(0, 0, 0, 0.12)'};
  background: ${({ $active }) =>
    $active ? 'rgba(45, 106, 79, 0.08)' : '#fff'};
  color: ${({ theme }) => theme.colours.broccoliGreen || '#1a1a1a'};
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 12px;
  cursor: pointer;
`

const PrimaryMark = styled.span`
  margin-left: 6px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  opacity: 0.7;
`

const LoadingWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 3rem;
  margin-bottom: 8px;
  font-size: 12px;
  color: ${({ theme }) => theme.colours.stormGrey || '#666'};
`

const ContextBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const Block = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const BlockTitle = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: ${({ theme }) => theme.colours.broccoliGreen || '#1a1a1a'};
`

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
  gap: 8px;
`

const Metric = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const MetricLabel = styled.span`
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: ${({ theme }) => theme.colours.stormGrey || '#666'};
`

const MetricValue = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colours.broccoliGreen || '#1a1a1a'};
`

const CompactList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const CompactItem = styled.li`
  padding: 6px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.04);

  &:last-child {
    border-bottom: none;
  }
`

const ItemMain = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colours.broccoliGreen || '#1a1a1a'};
  overflow: hidden;
  text-overflow: ellipsis;
`

const ItemMeta = styled.div`
  margin-top: 2px;
  font-size: 11px;
  color: ${({ theme }) => theme.colours.stormGrey || '#666'};
`

export default CatchBoardDealContextPanel
